// The ranked list behind SF1, SF2 and SF4.
//
// Shares sit beside counts everywhere, because the PRD's second principle is
// that ten thousand impressions sounds strong until it is four percent of the
// term. And a rate never appears without the volume it rests on.
import React from 'react';
import s from '../TermPerformance.module.css';
import { CLASS_LABEL, num, pct } from '../format';

const Rate = ({ value, volume, thin }) => (
  <span className={s.rate}>
    <span className={`${s.rateValue} ${thin ? s.rateThin : ''}`}>{pct(value)}</span>
    <span className={s.rateVolume}>
      {num(volume)} clicks{thin ? ' · held out of ranking' : ''}
    </span>
  </span>
);

const Movement = ({ value }) => {
  if (value == null) return <span className={s.flat}>new</span>;
  if (value === 0) return <span className={s.flat}>—</span>;
  return (
    <span className={value > 0 ? s.up : s.down}>
      {value > 0 ? '▲' : '▼'}{Math.abs(value)}
    </span>
  );
};

const ShareBar = ({ value }) =>
  value == null ? <span className={s.muted}>—</span> : (
    <span className={s.bar}>
      <span className={s.barTrack}>
        <span className={s.barFill} style={{ width: `${Math.min(100, value * 100)}%` }} />
      </span>
      {pct(value)}
    </span>
  );

const COLUMNS = {
  top: ['Clicks', 'Impr. share', 'Click share', 'Search volume', 'Click rate'],
  conversion: ['Conversion', 'Purchases', 'Market conv.', 'Clicks', 'Purch. share'],
  gaps: ['Impressions missed', 'Impr. share', 'Search volume', 'Clicks', 'Click share'],
};

export default function TermTable({ view, terms, onCorrect }) {
  if (!terms.length) return <div className={s.empty}>No search terms for this window.</div>;

  return (
    <div className={s.tableWrap}>
      <table className={s.table}>
        <thead>
          <tr>
            <th className={s.numeric}>#</th>
            <th>Search term</th>
            {view === 'top' && <th className={s.numeric}>Moved</th>}
            <th>Class</th>
            <th className={s.numeric}>Products</th>
            {(COLUMNS[view] || COLUMNS.top).map((c) =>
              <th key={c} className={s.numeric}>{c}</th>)}
          </tr>
        </thead>
        <tbody>
          {terms.map((t) => {
            const thin = !t.rates.rankable;
            return (
              <tr key={t.search_term}>
                <td className={s.numeric}>{t.rank}</td>
                <td className={s.term}>{t.search_term}</td>
                {view === 'top' && (
                  <td className={s.numeric}><Movement value={t.movement} /></td>
                )}
                <td>
                  {/* A seller who spots their own brand in the wrong bucket and
                      cannot fix it discounts the whole ratio. */}
                  <select className={s.classSelect} value={t.classification}
                          onChange={(e) => onCorrect(t.search_term, e.target.value)}
                          title="Correct this classification — it persists across periods">
                    {Object.entries(CLASS_LABEL).map(([k, v]) =>
                      <option key={k} value={k}>{v}</option>)}
                  </select>
                </td>
                <td className={s.numeric}>{t.products}</td>

                {view === 'conversion' ? (
                  <>
                    <td className={s.numeric}>
                      <Rate value={t.rates.conversion} volume={t.own.clicks} thin={thin} />
                    </td>
                    <td className={s.numeric}>{num(t.own.purchases)}</td>
                    <td className={s.numeric}>{pct(t.rates.market_conversion)}</td>
                    <td className={s.numeric}>{num(t.own.clicks)}</td>
                    <td className={s.numeric}><ShareBar value={t.shares.purchases} /></td>
                  </>
                ) : view === 'gaps' ? (
                  <>
                    <td className={s.numeric}>
                      {num(t.market.impressions - t.own.impressions)}
                    </td>
                    <td className={s.numeric}><ShareBar value={t.shares.impressions} /></td>
                    <td className={s.numeric}>{num(t.volume)}</td>
                    <td className={s.numeric}>{num(t.own.clicks)}</td>
                    <td className={s.numeric}><ShareBar value={t.shares.clicks} /></td>
                  </>
                ) : (
                  <>
                    <td className={s.numeric}>{num(t.own.clicks)}</td>
                    <td className={s.numeric}><ShareBar value={t.shares.impressions} /></td>
                    <td className={s.numeric}><ShareBar value={t.shares.clicks} /></td>
                    <td className={s.numeric}>{num(t.volume)}</td>
                    <td className={s.numeric}>
                      <Rate value={t.rates.click_rate} volume={t.own.clicks} thin={thin} />
                    </td>
                  </>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
